import { styled } from 'inlines'
import { colors } from '../utils/colors.js'
import { Button } from './Button.js'
import { Icon } from '../assets/icons/index.js'
import { MOBILE } from '../utils/breakpoints.js'
import { transparentColor } from '../utils/transparentcolor.js'
import { IconButton } from './IconButton.js'
import { useLocation } from 'wouter'
import { useVotingRegion } from '../hooks/useVotingRegion.js'
import { interpolate } from '../utils/interpolate.js'
import { usePhoneVotesCast } from '../hooks/usePhoneVotesCast.js'
import { useVotes } from '../hooks/useVotes.js'
import { Meter } from './Meter.js'

export const LastSlide = ({
  style,
  index,
  goToIndex,
  phoneContestant,
  dictionary,
}) => {
  const [, setLocation] = useLocation()

  const [{ voteType, maxVotes }] = useVotingRegion()
  const isOnline = voteType === 'online'

  const [, setVotes] = useVotes()

  const [phoneVotesCast] = usePhoneVotesCast()

  const amountOfPhoneVotesCast = Object.values(phoneVotesCast).reduce(
    (sum, n) => sum + n,
    0,
  )

  if (maxVotes === undefined) console.warn('LastSlide: maxVotes missing')
  const remainingVotes = Math.max(0, (maxVotes ?? 0) - amountOfPhoneVotesCast)

  const wrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: 24,
    padding: 48,
    backgroundColor: transparentColor(colors.secondary, 0.33),
    gap: 16,
    ...style,
  } as const

  const textWrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: 16,
    [MOBILE]: {
      flex: 1,
      justifyContent: 'center',
    },
  } as const

  const buttonsWrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    gap: 16,
  } as const

  const backButton = (
    <IconButton
      style={{
        position: 'absolute',
        display: 'none',
        top: 24,
        left: 24,
        width: 32,
        height: 32,
        borderRadius: 16,
        background:
          'linear-gradient(0deg, var(--White, rgba(255, 255, 255, 0.25)) 0%, var(--White, rgba(255, 255, 255, 0.25)) 100%)',
        [MOBILE]: {
          top: '52px',
          display: 'flex',
          zIndex: 1,
        },
      }}
      icon={<Icon type="backIcon" />}
      onClick={() => {
        if (index !== 0) {
          goToIndex(index - 1)
        }
      }}
    />
  )

  if (isOnline) {
    return (
      <styled.div style={wrapperStyle}>
        <styled.div style={textWrapperStyle}>
          <styled.h2 style={{ color: colors.primary }}>
            {dictionary.thankYouForVoting}
          </styled.h2>
          <styled.p>{dictionary.thankYouCardLimit}</styled.p>
        </styled.div>
        <styled.div style={buttonsWrapperStyle}>
          <Button
            id="thanks-page-action-back-home-button"
            style={{
              marginTop: 32,
              display: 'flex',
              justifyContent: 'center',
            }}
            fullWidth
            onClick={() => setLocation('/')}
          >
            {dictionary?.backToStart}
          </Button>
        </styled.div>
        {backButton}
      </styled.div>
    )
  }

  return (
    <styled.div style={wrapperStyle}>
      <styled.div style={textWrapperStyle}>
        <styled.h2 style={{ color: colors.primary }}>
          {dictionary.thankYouForVoting}
        </styled.h2>
        <styled.p>
          {interpolate(dictionary.thankYouCallSMSVoteLimit, {
            maxVotes: maxVotes ?? 0,
            votes: dictionary.votes,
          })}
        </styled.p>
        {remainingVotes > 0 ? (
          <>
            <Meter
              style={{
                marginTop: 16,
                width: '100%',
                maxWidth: 480,
                [MOBILE]: { maxWidth: '240px' },
              }}
              value={maxVotes ? (remainingVotes / maxVotes) * 100 : 0}
            />
            <styled.p>
              {interpolate(dictionary.remainingVotesCallSMS, {
                REMAINING_VOTES: remainingVotes,
              })}{' '}
              {dictionary.votes}
            </styled.p>
          </>
        ) : (
          <styled.p>
            {interpolate(dictionary.maxedOutNotice, {
              maxVotes: maxVotes ?? 0,
              votes: dictionary.votes,
            })}
          </styled.p>
        )}
      </styled.div>
      <styled.div style={buttonsWrapperStyle}>
        {remainingVotes > 0 && (
          <Button
            id="thanks-page-action-phonevote-again-button"
            style={{
              marginTop: 32,
              display: 'flex',
              justifyContent: 'center',
            }}
            fullWidth
            onClick={() => {
              const voteRegion = phoneContestant.votingRegion
              setVotes({ [voteRegion]: 1 })
              setLocation('/cart')
            }}
          >
            {dictionary.voteAgainButton} ·{' '}
            {phoneContestant?.name ? `${phoneContestant?.name}` : ''}
          </Button>
        )}
        <Button
          id="thanks-page-action-back-home-button"
          variant="border"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
          fullWidth
          onClick={() => setLocation('/')}
        >
          {remainingVotes > 0
            ? dictionary.voteForAnotherButton
            : dictionary?.backToStart}
        </Button>
      </styled.div>
      {backButton}
    </styled.div>
  )
}
